import type { MessageBubbleProps } from './types';
import { FilesPreview } from 'Components/Chat/ChatLayout/FilesPreview';
import { LinkPreview } from 'Components/Chat/ChatLayout/LinkPreview';
import { ChatLayoutContext } from 'Components/Chat/contexts/ChatLayoutContext';
import { COMMUNICATOR_REACTIONS } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { MobXProviderContext, observer } from 'mobx-react';
import { MessageModel } from 'Models/MessageModel';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useFetchMentionUsers } from 'Modules/conversation/store/mentions/index.requests';
import { EmojiReactionDisplay } from 'Modules/ui/emoji/emojiReactions/components/emojiReactionDisplay';
import React, { useMemo } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { useMarkdownCompiler } from 'Utils/markdownCompiler';
import { isLargeEmojiMessage } from 'Utils/isLargeEmojiMessage';
import { GifEmbed } from '~/modules/ui/gif';
import { cn } from '~/lib/utils';
import { Styled } from './index.styles';
import { MessageBubbleEditor } from './MessageBubbleEditor';
import { MessageStatus } from './MessageStatus';
import { PinTextButton } from './PinTextButton';
import { ThreeDotsButton } from './ThreeDotsButton';
import { usePinnedStatus } from './usePinnedStatus';

const testid = 'messageBubble';

export const MessageBubble = observer(
  ({
    AvatarComponent,
    content,
    conversationId,
    displayName,
    isOwnMessage = false,
    message,
    messageDate,
    isInactive = false,
    onClick,
  }: MessageBubbleProps) => {
    const {
      conversation,
      participant: { selectParticipantPersons },
      person: { loggedInPersonId },
    } = React.useContext<RootStoreProps>(MobXProviderContext);
    const [showThreeDots, setShowThreeDots] = React.useState(false);
    const [openThreeDots, setOpenThreeDots] = React.useState(false);
    const { editMessageId, view } = React.useContext(ChatLayoutContext);
    const { compileViewerMarkdown, compileEditorMarkdown } =
      useMarkdownCompiler(message.id, message.references);
    const { mentionUsers, addMentionUser } = useConversationStore();
    const { [COMMUNICATOR_REACTIONS]: isReactionsEnabled } = useFeatureFlags([
      COMMUNICATOR_REACTIONS,
    ]);

    const participants = useMemo(() => {
      return selectParticipantPersons(conversationId || '');
    }, [conversationId, selectParticipantPersons]);

    // Get mention person ids for people who are not participants anymore
    const mentionPersonIds = useMemo<string[]>(() => {
      const mentionPersonIds: string[] = [];

      if (content) {
        const mentions = content.matchAll(/@pr([0-9]\d*)/g);

        [...mentions].forEach((mention) => {
          const participantId = mention[1];

          const existingParticipant = participants?.find(
            (participant) => participant.person.sourceId === participantId
          );

          // If mentions person is a participant and is not already stored on mentionUsers, store it to avoid having to fetch again when a user leaves
          if (existingParticipant && !mentionUsers.has(participantId)) {
            addMentionUser(existingParticipant.person);
            return;
          }

          // Add mentioned person id to list of ids to fetch, unless it's already on participants list or on the mentionUsers list
          if (!existingParticipant && !mentionUsers.has(participantId)) {
            mentionPersonIds.push(participantId);
          }
        });
      }

      return mentionPersonIds;
    }, [addMentionUser, content, mentionUsers, participants]);

    useFetchMentionUsers(mentionPersonIds);

    const markdownSource = message.chat?.text_v2 || content || '';
    const showGifEmbed = Boolean(message.gif?.id && !markdownSource.trim());
    const largeEmoji = React.useMemo(() => {
      if (
        message.isDeleted ||
        showGifEmbed ||
        !!message.documents?.length ||
        !!message.gif?.id
      ) {
        return false;
      }

      return isLargeEmojiMessage(markdownSource);
    }, [
      markdownSource,
      message.documents?.length,
      message.gif?.id,
      message.isDeleted,
      showGifEmbed,
    ]);

    const compiledContent = React.useMemo(() => {
      if (showGifEmbed) {
        return null;
      }

      if (!markdownSource.trim()) {
        return null;
      }

      let compiledContent = compileViewerMarkdown(markdownSource);

      // compileViewerMarkdown adds \n\n to the end of strings, so we have to remove it, otherwise there will be a line break at the end of the message
      if (Array.isArray(compiledContent)) {
        compiledContent = compiledContent.map((element, index) => {
          if (typeof element === 'string' && element.endsWith('\n\n')) {
            return (
              <React.Fragment key={index}>
                {element.slice(0, -2)}
              </React.Fragment>
            );
          }

          return <React.Fragment key={index}>{element}</React.Fragment>;
        });
      }

      return compiledContent;
      // We only need to trigger compileViewerMarkdown when the content or the text_v2 of the message changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content, message.chat?.text_v2, showGifEmbed, markdownSource]);

    const { pinnedBy, togglePinMessage } = usePinnedStatus(
      message.id,
      conversationId
    );

    if (
      !content &&
      !message.gif?.id &&
      !message.documents?.length &&
      !message.isDeleted
    ) {
      return null;
    }

    /**
     * If the user is active participant and the message is not deleted,
     * and the message is not in sending status,
     * then the user can be shown the three dots.
     */
    let couldShowThreeDots = false;
    if (
      conversation?.CurrentConversation?.isActiveParticipant &&
      !message.isDeleted
    ) {
      if (message instanceof MessageModel) {
        couldShowThreeDots =
          message.internalStatus !== 'Sending' &&
          message.smsStatus !== 'Sending';
      } else {
        couldShowThreeDots = true;
      }
    }

    return editMessageId === message.id &&
      view !== 'pinned-messages' &&
      !message.gif?.id ? (
      <MessageBubbleEditor
        {...{ message, conversationId, compileEditorMarkdown }}
      />
    ) : (
      <Styled.ParentBox
        id={message.id}
        $isOwnMessage={isOwnMessage}
        $hasReactions={
          !!(message?.reactions && Object.keys(message.reactions).length > 0)
        }
      >
        {conversation?.CurrentConversation?.isActiveParticipant && pinnedBy && (
          <PinTextButton
            {...{ isOwnMessage, pinnedBy, togglePinMessage, testid }}
          />
        )}
        <Styled.BoxWithAvatar $isOwnMessage={isOwnMessage}>
          {AvatarComponent && !isOwnMessage && (
            <Styled.AvatarContainer>{AvatarComponent}</Styled.AvatarContainer>
          )}

          <Styled.Box
            data-testid="message-box-test-id"
            $isOwnMessage={isOwnMessage}
            onMouseEnter={() => setShowThreeDots(couldShowThreeDots)}
            onMouseLeave={() => !openThreeDots && setShowThreeDots(false)}
            {...{ onClick }}
          >
            <Styled.NameDateContainer>
              <Styled.Name
                $showThreeDots={showThreeDots}
                variant="paragraph"
                noMargin
              >
                {displayName}
                {isInactive && ' [Inactive]'}
              </Styled.Name>

              <Styled.Date variant="subtitles" noMargin>
                {messageDate}
              </Styled.Date>

              {message?.updated && !message.sms && !message.isDeleted && (
                <Styled.EditTag variant="subtitles" noMargin>
                  Edited
                </Styled.EditTag>
              )}

              {showThreeDots && (
                <ThreeDotsButton
                  {...{
                    conversationId,
                    isOwnMessage,
                    message,
                    openThreeDots,
                    setOpenThreeDots,
                    setShowThreeDots,
                    testid,
                    togglePinMessage,
                    pinnedBy,
                  }}
                />
              )}
            </Styled.NameDateContainer>

            <Styled.Message
              variant="paragraph"
              $largeEmoji={largeEmoji}
              data-testid={`${isOwnMessage ? 'own' : 'other'}-message-bubble`}
            >
              {message.isDeleted ? (
                <i>This message was deleted</i>
              ) : showGifEmbed && message.gif?.id ? (
                <div className="flex justify-center">
                  <GifEmbed gifId={message.gif.id} />
                </div>
              ) : (
                compiledContent
              )}
            </Styled.Message>

            {!!message.documents?.length && (
              <FilesPreview {...{ message, isOwnMessage }} />
            )}

            {!message?.isDeleted && message?.linkPreview?.url && (
              <LinkPreview {...message.linkPreview} />
            )}
          </Styled.Box>
        </Styled.BoxWithAvatar>

        {!message?.isDeleted &&
          message?.id &&
          isReactionsEnabled &&
          message?.reactions && (
            <EmojiReactionDisplay
              testID="message"
              reactions={message.reactions}
              messageId={message.id}
              className={cn(
                '-mt-4',
                AvatarComponent && !isOwnMessage ? 'pl-[45px]' : 'pl-1'
              )}
            />
          )}

        {message instanceof MessageModel &&
          message.personId === loggedInPersonId && (
            <MessageStatus message={message} />
          )}
      </Styled.ParentBox>
    );
  }
);
